import { AuditSection } from "../models/lab-audit.models";

export type EquipmentCellKind = "static" | "text" | "answer" | "choice" | "rating";

export interface EquipmentColumn {
  key: string;
  label: string;
  kind: EquipmentCellKind;
  options?: string[];
}

export interface EquipmentRow {
  id: string;
  values?: Record<string, string>;
  scored?: boolean;
}

export interface EquipmentGroup {
  id: string;
  title: string;
  countLabel?: string;
  columns: EquipmentColumn[];
  rows: EquipmentRow[];
}

export interface EquipmentSheetDefinition {
  section: Extract<AuditSection, "labEquipment1" | "labEquipment2" | "labEquipment3">;
  title: string;
  scoreLabel: string;
  groups: EquipmentGroup[];
}

const text = (key: string, label: string): EquipmentColumn => ({ key, label, kind: "text" });
const fixed = (key: string, label: string): EquipmentColumn => ({ key, label, kind: "static" });
const answer = (label = "Yes / No"): EquipmentColumn => ({ key: "answer", label, kind: "answer" });
const rating: EquipmentColumn = { key: "rating", label: "Rating", kind: "rating" };

function blankRows(prefix: string, count: number): EquipmentRow[] {
  return Array.from({ length: count }, (_, index) => ({ id: `${prefix}-${index + 1}` }));
}

export const LAB_EQUIPMENT_1: EquipmentSheetDefinition = {
  section: "labEquipment1",
  title: "Lab Equipment 1",
  scoreLabel: "Score Lab Equip 1",
  groups: [
    {
      id: "consistometers",
      title: "1 - Consistometers",
      countLabel: "Total Number of consistometers",
      columns: [
        text("brand", "Brand"), text("type", "Type"), text("model", "Model"),
        text("serialNumber", "Serial Number"), text("temperatureRating", "Temperature Rating"),
        text("pressureRating", "Pressure Rating"), text("lastCalibration", "Last Date of Calibration"),
        text("oilType", "Type of oil"),
      ],
      rows: blankRows("consistometer", 8),
    },
    {
      id: "calibration-tags",
      title: "Calibration Tags",
      columns: [fixed("item", "Check"), answer(), text("comments", "Comments")],
      rows: [{ id: "consistometer-calibration-tags", values: { item: "Are Calibration Tags Up to date?" } }],
    },
    {
      id: "consistometer-calibration",
      title: "Calibration",
      columns: [fixed("item", "Calibration"), fixed("frequency", "Frequency"), answer(), text("comments", "Comments"), rating],
      rows: [
        { id: "consistometer-temperature", scored: true, values: { item: "Temperature calibration", frequency: "+/-3 F or 1 C", comments: "Inspected dry block and calibration date." } },
        { id: "consistometer-pressure", scored: true, values: { item: "Pressure Calibration", frequency: "+/- 250 psi", comments: "Annual pressure check performed by vendors." } },
        { id: "consistometer-potentiometer", scored: true, values: { item: "Potentiometer", frequency: "By Voltage?", comments: "Inspected weights." } },
        { id: "consistometer-timer", scored: true, values: { item: "Timer", frequency: "+/- 30 sec per hour" } },
        { id: "consistometer-speed", scored: true, values: { item: "Speed", frequency: "+/- 150 RPM" } },
      ],
    },
    {
      id: "atmospherics",
      title: "Atmospherics",
      columns: [text("count", "How many are Atmospherics")],
      rows: [{ id: "atmospherics-count" }],
    },
  ],
};

export const LAB_EQUIPMENT_2: EquipmentSheetDefinition = {
  section: "labEquipment2",
  title: "Lab Equipment 2",
  scoreLabel: "Score Lab Equip 2",
  groups: [
    {
      id: "uca",
      title: "2 - Ultrasonic Cements Analyzers",
      countLabel: "Total Number of UCA'S",
      columns: [
        text("brand", "Brand"), text("controller", "Controller"), text("model", "Model"),
        text("serialNumber", "Serial Number"), text("temperatureRating", "Temperature Rating"),
        text("pressureRating", "Pressure Rating"), text("lastCalibration", "Last Date of Calibration"),
      ],
      rows: blankRows("uca", 12),
    },
    {
      id: "uca-calibration-tags",
      title: "Calibration Tags",
      columns: [fixed("item", "Check"), answer(), text("comments", "Comments")],
      rows: [{ id: "uca-calibration-tags", values: { item: "Are Calibration Tags Up to date" } }],
    },
    {
      id: "uca-calibration",
      title: "Calibration",
      columns: [fixed("item", "Calibration"), fixed("frequency", "Frequency"), answer(), text("comments", "Comments"), rating],
      rows: [
        { id: "uca-temperature", scored: true, values: { item: "Temperature calibration", frequency: "+/-3 F or 1 C", comments: "Inspected dry block and calibration date." } },
        { id: "uca-pressure", scored: true, values: { item: "Pressure Calibration", frequency: "+/- 250 psi", comments: "Annual pressure check performed by vendors." } },
        { id: "uca-timer", scored: true, values: { item: "Timer", frequency: "+/- 30 sec per hour" } },
      ],
    },
    {
      id: "rheometers",
      title: "3 - Rheometers",
      countLabel: "How Many Rheometers Do the Lab Have",
      columns: [text("modelAndSerial", "Model & S/N"), text("lastCalibration", "Last Calibration Date"), { key: "springs", label: "Number of Springs", kind: "choice", options: ["1", "2"] }],
      rows: blankRows("rheometer", 3),
    },
    {
      id: "rheometer-calibration",
      title: "Calibration",
      columns: [fixed("item", "Calibration"), fixed("frequency", "Frequency"), answer(), text("comments", "Comments"), rating],
      rows: [
        { id: "rheometer-torque", scored: true, values: { item: "Torque", frequency: "Manufacturer Recomm" } },
        { id: "rheometer-speed", scored: true, values: { item: "Speed", frequency: "+/- 15 RPM" } },
        { id: "rheometer-alignment", scored: true, values: { item: "Alignment", frequency: "Verify Center" } },
      ],
    },
  ],
};

export const LAB_EQUIPMENT_3: EquipmentSheetDefinition = {
  section: "labEquipment3",
  title: "Lab Equipment 3",
  scoreLabel: "Score Lab Equip 3",
  groups: [
    {
      id: "scales",
      title: "4 -Scales",
      countLabel: "Total Number of Scales",
      columns: [
        text("brand", "Brand"), text("model", "Model"), text("serialNumber", "Serial Number"),
        { key: "scaleType", label: "Mechanical / Electronic", kind: "choice", options: ["Mechanical", "Electronic"] },
        text("accuracy", "Accuracy (Decimals)"),
        { key: "calibrationType", label: "Calibration", kind: "choice", options: ["Internal", "External"] },
        text("lastCalibration", "Last Date of calibration"), rating,
      ],
      rows: blankRows("scale", 4).map((row, index) => ({ ...row, scored: index < 2 })),
    },
    {
      id: "blenders",
      title: "5 - Blenders",
      countLabel: "Total Number of Blenders",
      columns: [
        text("model", "Model"), text("type", "Type"), text("serialNumber", "Serial Number"),
        { key: "hardenedBlade", label: "Hardened blade assembly", kind: "choice", options: ["Yes", "No"] },
        { key: "assemblyClean", label: "Is the Assembly Clean?", kind: "choice", options: ["Yes", "No"] },
        text("mix1", "Motor's RPM Mix 1"), text("mix2", "Motor's RPM Mix 2"), text("bladeWeight", "Blade Wt"), rating,
      ],
      rows: blankRows("blender", 4).map(row => ({ ...row, scored: true })),
    },
    {
      id: "fluid-loss-cells",
      title: "6 - Fluid Loss Cells",
      countLabel: "Total Number of Fluid Loss Cell",
      columns: [
        text("brand", "Brand"), text("type", "Type"), text("model", "Model"), text("serialNumber", "Serial Number"),
        text("temperatureRating", "Temperature Rating"), text("pressureRating", "Pressure Rating"),
        { key: "backPressureValve", label: "Back Press Regulator Valve", kind: "choice", options: ["Yes", "No"] },
        { key: "nitrogenBottle", label: "Nitrogen Bottle", kind: "choice", options: ["Outside Lab", "Next to"] }, rating,
      ],
      rows: blankRows("fluid-loss-cell", 5).map(row => ({ ...row, scored: true })),
    },
    {
      id: "curing-chambers",
      title: "7 - Curing Chambers",
      columns: [
        text("brand", "Brand"), text("type", "Type"), text("model", "Model"), text("serialNumber", "Serial Number"),
        text("temperatureRating", "Temperature Rating"), text("pressureRating", "Pressure Rating"),
        { key: "cells", label: "Cells", kind: "choice", options: ["Single", "Dual"] },
        { key: "vessel", label: "Vessel", kind: "choice", options: ["Horizontal", "Vertical"] }, rating,
      ],
      rows: blankRows("curing-chamber", 3).map(row => ({ ...row, scored: true })),
    },
    {
      id: "curing-water-bath",
      title: "Curing Chambers - Water Bath",
      columns: [fixed("item", "Check"), answer(), rating],
      rows: [{ id: "curing-water-bath", scored: true, values: { item: "Is there a water bath for the cubes?" } }],
    },
    {
      id: "miscellaneous",
      title: "Miscellaneous Equipment",
      columns: [fixed("item", "Equipment / Check"), answer(), text("comments", "Comments"), rating],
      rows: [
        { id: "misc-mud-balance", scored: true, values: { item: "Mud Balance", comments: "Quantity:" } },
        { id: "misc-pressurized-mud-balance", scored: true, values: { item: "Pressurized Mud Balance", comments: "Quantity:                  o-rings good" } },
        { id: "misc-free-fluid", values: { item: "Free Fluid - using glass cylinders?" } },
        { id: "misc-long-cylinders", values: { item: "Is Lab Personnel using 250ml Long Cylinders" } },
        { id: "misc-sealed-cylinders", scored: true, values: { item: "Are cylinders sealed and have 2 mL increments or less?", comments: "Rack available, in good condition?" } },
        { id: "misc-angle", scored: true, values: { item: "Are free fluid tests ran @ 45° angle?", comments: "Good rack design to use at multiple angles." } },
        { id: "misc-oven", scored: true, values: { item: "Oven" } },
      ],
    },
  ],
};